import {Injectable, OnDestroy, signal} from "@angular/core";
import {__} from "@app/shared/functions/object.functions";
import {Howl} from "howler";
import {AudioPlayerService} from "@app/shared/components/audio-player-control/audio-player.service";
import {MetricsService} from "../shared/services/metrics.service";
import {Observable, Subject} from "rxjs";
import {EventModel, HowlerEvents} from "@app/shared/base/models/howler-event";
import {MatSliderDragEvent} from "@angular/material/slider";
import {Logger} from "@app/core/logger.service";
import {Episode} from "@app/shared/models/classes/Episode";

const log = new Logger("CoyoPluginService");

@Injectable({
  providedIn: "root",
})
export class CoyoPluginService implements OnDestroy {
  // Hard ceiling for the load phase: html5-streaming Howler does not reliably
  // fire `loaderror` for dead presigned URLs, so without this the loading
  // spinner can run forever.
  static readonly LOAD_TIMEOUT_MS = 15000;

  sound!: Howl | null;
  duration!: number;
  private isListened = false;
  private streamStart: any;
  private secondsStreamed!: number;
  private streamEnd: any;
  private trackListenInterval: any;

  private previousEpisodeId!: string;
  private playErrorRetryCount = 0;

  isLoading$: Subject<EventModel> = new Subject<EventModel>();
  isDisabled$: Subject<EventModel> = new Subject<EventModel>();
  howlerEvents$: Subject<EventModel> = new Subject<EventModel>();

  private readonly currentPlayingEpisodeState = signal<Episode | null>(null);
  readonly currentPlayingEpisode = this.currentPlayingEpisodeState.asReadonly();
  private readonly currentPlayingEpisodeChanges = new Subject<Episode | null>();
  readonly currentPlayingEpisode$ = new Observable<Episode | null>((subscriber) => {
    subscriber.next(this.currentPlayingEpisodeState());
    return this.currentPlayingEpisodeChanges.subscribe(subscriber);
  });

  private readonly playbackSpeedState = signal<number>(1);
  readonly playbackSpeed = this.playbackSpeedState.asReadonly();
  private readonly playbackSpeedChanges = new Subject<number>();
  readonly playbackSpeed$ = new Observable<number>((subscriber) => {
    subscriber.next(this.playbackSpeedState());
    return this.playbackSpeedChanges.subscribe(subscriber);
  });

  constructor(private audioPlayerService: AudioPlayerService, private metricsService: MetricsService) {}

  private pluginSource = 'coyo';

  private trackStream($event: boolean, episode_id: string, token: string): void {
    if ($event === false) {
      this.metricsService.createNewStreamFromPlugin(episode_id, token, this.pluginSource).subscribe({
        next: (result: boolean) => {},
        // Metrics are best-effort: a failed call (e.g. the server's
        // PK_PluginUsersInEpisodes duplicate-key 500 on a repeated stream) must
        // not surface as an uncaught error and disrupt playback.
        error: (err) => log.warn(`Stream metric failed`, {episode_id, err}),
      });
    }
  }

  private trackListen(value: number, episode_id: string, token: string): void {
    if (value > 0) {
      this.metricsService.createNewListenFromPlugin(episode_id, String(value), token, this.pluginSource).subscribe({
        next: (result: boolean) => {},
        // Best-effort metric — swallow + log errors so the listen interval
        // never throws an uncaught error.
        error: (err) => log.warn(`Listen metric failed`, {episode_id, err}),
      });
    }
  }

  get seek() {
    return this.sound ? this.sound.seek() : 0;
  }

  set seek(position: number) {
    if (!this.sound) return;
    this.sound.seek(position);
  }

  get durationS() {
    if (!this.sound) return 0;
    return this.sound.duration();
  }

  loadAudio(
    url: string,
    episode_id: string,
    token: string,
    source?: string,
    refreshUrl?: () => Observable<string>
  ): void {
    if (source) {
      this.pluginSource = source;
    }

    if (!__.IsNullOrUndefined(this.sound)) {
      this.pause();
      this.streamEnd = this.sound.seek();
      this.sound.unload();
      this.sound = null;
    }

    // If this is a new episode, track the stream
    if (episode_id !== this.previousEpisodeId) {
      this.trackStream(false, episode_id, token); // Tracking the new stream
    }

    if (this.previousEpisodeId) {
      this.onPause(this.previousEpisodeId, token);
      this.howlerEvents$.next({value: HowlerEvents.End, episode_id: this.previousEpisodeId});
    }

    setTimeout(() => {
      if (!__.IsNullOrUndefined(episode_id)) {
        this.isLoading$.next({value: true, episode_id});
        this.startLoad(url, episode_id, token, refreshUrl, false);
      } else {
        log.error(`No episode_id provided for audio loading`);
        throw new Error("[AUDIO PLAYER] No source was provided");
      }
    }, 0);
  }

  /**
   * Builds the Howl and supervises loading. Two failure paths end the spinner:
   * `loaderror` and a hard timeout — html5-streaming Howler does not reliably
   * fire `loaderror` for dead presigned URLs (S3 403 surfaces as an opaque
   * CORS failure), which used to leave the loading state spinning forever.
   * On the first failure we fetch a fresh URL via `refreshUrl` (bypassing the
   * http cache, where an expired presigned URL is the most likely culprit)
   * and retry once; a second failure gives up and disables the entry.
   */
  private startLoad(
    url: string,
    episode_id: string,
    token: string,
    refreshUrl: (() => Observable<string>) | undefined,
    isRetry: boolean
  ): void {
    const loadStartTime = Date.now();
    let settled = false;

    const giveUp = (): void => {
      this.isDisabled$.next({value: true, episode_id});
      this.isLoading$.next({value: false, episode_id});
      this.duration = 0;
    };

    const failOnce = (origin: string, error?: unknown): void => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(loadTimeout);

      const loadDuration = Date.now() - loadStartTime;
      log.error(`Audio loading failed`, {
        episode_id,
        origin,
        error,
        isRetry,
        loadDuration: `${loadDuration}ms`,
        url: url?.substring(0, 50) + '...',
        errorTime: new Date().toISOString()
      });

      if (!isRetry && !__.IsNullOrUndefined(refreshUrl)) {
        // Expired presigned URL is the most common cause — fetch a fresh one
        // (cache-bypassing) and retry once. The spinner keeps running, which
        // is honest: we are still trying.
        if (!__.IsNullOrUndefined(this.sound)) {
          this.sound.unload();
          this.sound = null;
        }
        refreshUrl().subscribe({
          next: (freshUrl: string) => {
            if (!__.IsNullOrUndefinedOrEmpty(freshUrl)) {
              log.warn(`Retrying audio load with a freshly fetched URL`, {episode_id});
              this.startLoad(freshUrl, episode_id, token, refreshUrl, true);
            } else {
              giveUp();
            }
          },
          error: () => giveUp(),
        });
      } else {
        giveUp();
      }
    };

    const loadTimeout = setTimeout(() => failOnce('timeout'), CoyoPluginService.LOAD_TIMEOUT_MS);

    this.sound = new Howl({
      src: [url],
      html5: true,
      preload: true,
      onloaderror: (id, error) => {
        // Check if this might be due to expired presigned URL
        if (error && (error.toString().includes('403') || error.toString().includes('401'))) {
          log.warn(`Potential expired presigned URL detected for audio`, {
            episode_id,
            error,
            message: 'This might indicate an expired or invalid presigned URL'
          });
        }
        failOnce('loaderror', error);
      },
      onplay: () => {
        this.playErrorRetryCount = 0;

        this.audioPlayerService.play();
        this.howlerEvents$.next({value: HowlerEvents.OnPlay, episode_id});
        this.trackStream(this.isListened, episode_id, token);
        this.isListened = true;
        this.streamStart = this.sound!.seek();
        this.previousEpisodeId = episode_id;

        this.trackListenInterval = setInterval(() => {
          this.streamStart = this.sound!.seek();
          this.trackListen(5, episode_id, token);
        }, 5000);
      },
      onend: () => {
        this.onEnd(episode_id, token);
      },
      onpause: () => {
        this.onPause(episode_id, token);
      },
      onplayerror: () => {
        if (this.playErrorRetryCount < 3) {
          this.playErrorRetryCount++;
          log.warn(`Play error detected – attempting unlock & resume (attempt ${this.playErrorRetryCount}/3)`, {episode_id});
          this.sound!.once('unlock', () => {
            this.sound!.play();
          });
        } else {
          log.error(`Play error retry limit reached – giving up`, {episode_id});
        }
      },
      onseek: () => {
        this.howlerEvents$.next({value: HowlerEvents.OnSeek, episode_id});
      },
    });

    if (this.sound.state() === "loaded") {
      this.howlerEvents$.next({value: HowlerEvents.AfterLoad, episode_id});
    }

    this.sound.once("load", () => {
      settled = true;
      clearTimeout(loadTimeout);
      this.duration = this.sound!.duration();

      this.howlerEvents$.next({value: HowlerEvents.AfterLoad, episode_id});
      if (this.sound!.playing() === false && this.duration > 0) this.sound!.play();
    });

    this.sound.once("end", () => {
      this.onPause(episode_id, token);
      this.howlerEvents$.next({value: HowlerEvents.End, episode_id});
    });
  }

  private onPause(episode_id: string, token: string): void {

    if (this.trackListenInterval) {
      clearInterval(this.trackListenInterval);
    }

    this.audioPlayerService.pause();

    if (!__.IsNullOrUndefined(this.sound)) {
      this.streamEnd = this.sound.seek();
    }
    if (this.streamStart < this.streamEnd) {
      this.secondsStreamed = Math.abs(Math.round(this.streamEnd - this.streamStart));
    } else {
      this.secondsStreamed = 0;
    }

    this.trackListen(this.secondsStreamed, episode_id, token);
    this.howlerEvents$.next({value: HowlerEvents.OnPause, episode_id});
  }

  onEnd(episode_id: string, token: string): void {
    if (this.trackListenInterval) {
      clearInterval(this.trackListenInterval);
    }

    this.secondsStreamed = Math.abs(Math.round(this.duration - this.streamStart));
    this.sound!.stop();

    this.trackListen(this.secondsStreamed, episode_id, token);
  }

  pause(): void {
    if (!this.sound) return;
    if (this.sound.playing() === true && this.duration > 0) {
      this.sound.pause();
    }
  }

  play(): void {
    if (!this.sound) return;
    if (this.sound.playing() === false) {
      this.sound.play();
    }
  }

  rate(playbackSpeed: number): void {
    if (!this.sound) return;
    this.sound.rate(playbackSpeed);
    this.setPlaybackSpeed(playbackSpeed);
  }

  skipBackward(): void {
    if (!this.sound) return;
    const currentPosition = this.sound.seek() as number;
    const position = currentPosition - 30;
    const newPosition = position > 0 ? position : 0;

    this.sound.seek(newPosition);
  }

  skipForward(): void {
    if (!this.sound) return;
    const currentPosition = this.sound.seek() as number;
    const newPosition = currentPosition + 30;

    this.sound.seek(newPosition);
  }

  ngOnDestroy(): void {
    if (this.trackListenInterval) {
      clearInterval(this.trackListenInterval);
      this.trackListenInterval = null;
    }
    this.clearSound();
  }

  clearSound() {
    if (!__.IsNullOrUndefined(this.sound)) {
      this.sound.stop();
      this.sound.unload();
    }
  }

  adaptVolume($event: MatSliderDragEvent): void {
    if (!this.sound) return;
    this.sound.volume($event.value);
  }

  setCurrentPlayingEpisode(episode: Episode | null): void {
    this.currentPlayingEpisodeState.set(episode);
    this.currentPlayingEpisodeChanges.next(episode);
  }

  getCurrentPlayingEpisode(): Episode | null {
    return this.currentPlayingEpisodeState();
  }

  private setPlaybackSpeed(playbackSpeed: number): void {
    this.playbackSpeedState.set(playbackSpeed);
    this.playbackSpeedChanges.next(playbackSpeed);
  }
}
